import { IRemoteMediaSyncManager, IMediaSyncInfo, MediaCmd, IRemoteMediaSyncManagerInjectable, IServices } from './interface';
import MeetingEventBus from '@src/page/toc/common/meeting-room-manager/components/eventBus';
import { MEETING_SCENE_SOURCE, PlayerAction, RoomAction } from '@src/page/toc/common/meeting-room-manager/message_const';
import playerReduxStore from '@player/src/store';
import { PlayerHelper } from '@src/framework/meeting-room-slidePlayer/playerEventHandler';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IMediaManager, MediaRegion } from '@src/slide/application/service/media-manager/media-manager.interface';
import { IUiDataService } from '@src/slide/domain/ui-model/service/ui-data-service.interface';
import { H5_PLAY_EVENT } from '@src/slide/domain/ui-model/element-modeles/property/attachment/constants';
import type { SuperPlayer } from '@src/slide/domain/ui-model/element-modeles/property/attachment/interface';
import type { ILiveManager } from '@src/slide/application/service/live-manager/core/interface';
import { PlayerType } from '../../note-view-media-sync-manager/core/note-view-media-sync-manager.interface';

export class RemoteMediaSyncManager implements IRemoteMediaSyncManager {
    // sceneSource用于判断是否处于远程演示场景
    private sceneSource: MEETING_SCENE_SOURCE = MEETING_SCENE_SOURCE.None;
    private mediaManager: IMediaManager;
    private uiDataService: IUiDataService;
    private liveManager: ILiveManager;
    private injectable: IRemoteMediaSyncManagerInjectable;

    public constructor(injectable: IRemoteMediaSyncManagerInjectable) {
        this.injectable = injectable;
    }

    /** 初始化数据 */
    public init = (sceneSource: MEETING_SCENE_SOURCE): void => {
        this.sceneSource = sceneSource;
    };

    /** 重置数据，每次退出远程演示都需要重置数据 */
    public reset = (): void => {
        this.sceneSource = MEETING_SCENE_SOURCE.None;
    };

    public setServiceDelegates = (services: IServices) => {
        this.mediaManager = services.mediaManager;
        this.uiDataService = services.uiDataService;
        services.liveManager.promise().then((liveManager) => {
            this.liveManager = liveManager;
        });
    };

    /**
     * 播放器监听媒体的行为，回调向房间层发送消息
     * @param mediaInstance media实例
     * @param mediaId mediaId
     * @returns 存放解绑函数的数组
     */
    public attachMediaEventInPlayer = (mediaInstance, mediaId: string, playType: PlayerType): (() => void)[] => {
        if (this.injectable.attachMediaEventInPlayer(playType) || this.notAttach()) return [() => {}];
        // 基于superPlayer现在的配置，每次seek都会导致play，监听seek会导致消息分别通过seek 和 play各发送一次，
        // 两次的消息同步其实没必要，因此取消seek的监听，每次seek时，通过play 和playtime 进行一次消息同步即可
        const mediaEventMap = new Map([
            [MediaCmd.PLAY, H5_PLAY_EVENT.VIDEO_PLAYING],
            [MediaCmd.PAUSE, H5_PLAY_EVENT.VIDEO_PAUSE],
            [MediaCmd.SPEED, H5_PLAY_EVENT.VIDEO_RATE_CHANGE],
            // [MediaCmd.SEEK, H5_PLAY_EVENT.VIDEO_SEEKED],
        ]);

        const detachedFuncList: (() => void)[] = [];

        mediaEventMap.forEach((mediaEvent, cmd) => {
            const callback = ({ target }) => this.sendPlayerEvent2Room(target, cmd, mediaId);
            mediaInstance.on(mediaEvent, callback);

            detachedFuncList.push(() => mediaInstance.off(mediaEvent, callback));
        });

        return detachedFuncList;
    };

    /**
     * 房间层监听播放器发来的消息，回调通过LiveManager向服务器发起请求
     * @returns 解绑函数
     */
    public attachRecvPlayerEventInRoom = (): () => void => {
        if (!this.isRemotePlay()) return () => {};
        return MeetingEventBus.onRecvPlayerEvent(PlayerAction.Media, async (mediaInfo) => {
            this.liveManager?.sendMediaLiveMsg(mediaInfo);
        });
    };

    /**
     * LiveManager监听自身发来的消息（媒体状态的变化），回调将服务器返回的消息推送给播放器
     * @returns 解绑函数
     */
    public attachLiveManagerMediaEvent = (): () => void => {
        if (!this.isRemotePlay()) return () => {};
        const MediaStateChanged = 'MediaStateChanged';

        this.liveManager.event.on(MediaStateChanged, this.sendMediaChangeToPlayer);

        return () => this.liveManager.event.off(MediaStateChanged, this.sendMediaChangeToPlayer);
    };

    /**
     * 播放器监听媒体状态改变，回调同步主讲人视频播放状态
     * @param slideStore
     * @returns 解绑函数
     */
    public attachRecvMediaChangeInPlayer = (): () => void => {
        if (!this.isRemotePlay()) return () => {};
        return MeetingEventBus.onRecvRoomEvent(
            RoomAction.MediaStateChanged,
            (mediaInfo: IMediaSyncInfo) => this.syncMediaChange(mediaInfo),
        );
    };

    /**
     * 播放器层向房间层发送消息
     * @param target media实例
     * @param cmd 播放/暂停/快进退/倍速
     * @param mediaId mediaId
     */
    private sendPlayerEvent2Room(target: SuperPlayer, cmd: MediaCmd, mediaId: string): void {
        let syncParams: IMediaSyncInfo = { cmd, mediaId, playtime: target.playtime };
        if (cmd === MediaCmd.SPEED) {
            syncParams = { ...syncParams, speed: target.playbackRate };
        }
        MeetingEventBus.sendPlayerEvent2Room(PlayerAction.Media, syncParams);
    }

    /**
     * 将服务器返回的消息推送给播放器
     * @param mediaInfo 需同步的消息
     */
    private sendMediaChangeToPlayer = (mediaInfo: IMediaSyncInfo) => {
        if (!this.liveManager.isAudience) return;
        MeetingEventBus.sendRoomEvent2Player(RoomAction.MediaStateChanged, mediaInfo);
    };

    /**
     * 同步主讲人的视频播放状态
     * @param mediaInfo
     * @param slideStore
     */
    private syncMediaChange(mediaInfo: IMediaSyncInfo): void {
        const { mediaId, cmd, playtime, speed } = mediaInfo;
        const [mediaIns, pictureModelKey] = this.findMediaById(mediaId) || [];
        if (!pictureModelKey) return;
        const handlerMap: Map<MediaCmd, () => void> = new Map([
            [MediaCmd.PLAY, () => this.playHandler(mediaIns as SuperPlayer | null, pictureModelKey, playtime)],
            [MediaCmd.PAUSE, () => this.pauseHandler(mediaIns as SuperPlayer | null)],
            [MediaCmd.SPEED, () => this.speedHandler(mediaIns as SuperPlayer | null, speed)],
            // [MediaCmd.SEEK, () => this.seekHandler(mediaIns, playtime)],
        ]);
        handlerMap.get(cmd)?.();
    }

    /**
     * 找到需要同步状态的media实例
     * @param mediaId
     * @param slideStore
     * @returns media实例 || null
     */
    private findMediaById(mediaId): [SuperPlayer | null, string] | null {
        if (!this.liveManager?.isAudience) return null;
        const masterPictureId = mediaId;
        // 观众和主讲人不在同一页时，观众无需响应主讲人视频状态的变化；
        const masterCurrentPage = this.getMasterCurrentPage();
        if (masterCurrentPage !== this.getAudienceCurrentPage()) return null;
        const currentSlideModel = this.uiDataService.presentation.children[masterCurrentPage];
        const findModel = uiModel => uiModel.id === masterPictureId && uiModel.type === UIModelType.IMAGE;
        const pictureModel = currentSlideModel.children?.find(findModel);
        if (!pictureModel) return null;

        const pictureModelKey = pictureModel.key;
        // 接新附件之后，视频默认是不加载的，只有开始播放之后才会获取到视频实例，所以 videoIns 大概率是没有的
        const videoIns = this.mediaManager?.getVideoInstanceByRegion(
            pictureModelKey,
            MediaRegion.PLAYER,
        );
        if (videoIns) {
            this.mediaManager?.updateElementKeyByRegion(pictureModelKey, MediaRegion.PLAYER);
        }
        return [videoIns as SuperPlayer | null, pictureModelKey];
    }

    /**
     * 获取主讲人当前页
     * @returns 主讲人当前页
     */
    private getMasterCurrentPage(): number {
        return PlayerHelper.getInstance().roomState.currentPage;
    }

    /**
     * 获取观众当前页
     * @returns 观众当前页
     */
    private getAudienceCurrentPage(): number {
        const control = playerReduxStore.getState().control as { currentPage: number };
        return control.currentPage;
    }

    /**
     * 从指定时间播放视频
     * @param mediaIns media实例
     * @param playtime 起播时间
     */
    private playHandler(mediaIns: SuperPlayer | null, pictureModelKey: string, playtime: number): void {
        if (!mediaIns) {
            this.mediaManager?.playVideoByRegion(pictureModelKey, MediaRegion.PLAYER);
            return;
        }
        // 由于play处理快进快退时会重新加载视频play({ url: mediaIns.url }, { startTime: playtime })
        // 此处用seek代替play
        if (!mediaIns.duration) {
            // 处理视频播放完之后，重新播放
            mediaIns.replay();
        } else {
            mediaIns.seek(playtime);
            // seek 调用后好像不自动播放，再调下 play
            mediaIns.play();
        }
    }

    /**
     * 暂停播放
     * @param mediaIns
     */
    private pauseHandler(mediaIns: SuperPlayer | null): void {
        if (mediaIns) {
            mediaIns.pause();
        }
    }

    /**
     * 快进/快退到起播时间
     * @param mediaIns media实例
     * @param playtime 起播时间
     */
    private seekHandler(mediaIns: SuperPlayer | null, playtime?: number): void {
        if (mediaIns) {
            mediaIns.seek(playtime ?? 0);
        }
    }

    /**
     * 倍速播放
     * @param mediaIns media实例
     * @param speed 速度数值
     */
    private speedHandler(mediaIns: SuperPlayer | null, speed?: number): void {
        if (mediaIns) {
            mediaIns.playbackRate = speed ?? 1;
        }
    }

    /**
     * 判断是否在远程演示场景
     * @returns boolean
     */
    private isRemotePlay(): boolean {
        return [MEETING_SCENE_SOURCE.MeetingRoom, MEETING_SCENE_SOURCE.InEditor].includes(this.sceneSource);
    }

    /**
     * 非远程演示场景，或者观众视角不需要监听media状态变化
     * @returns boolean
     */
    private notAttach(): boolean {
        return !this.isRemotePlay() || !!this.liveManager?.isAudience;
    }
}
